import { useCallback, useEffect, useState } from 'react';
import { type BriefingSnapshot, sitrepReportVersionLabel } from '@tsr/shared-types';
import {
  fetchBriefingSnapshot,
  fetchBriefingTokenPayload,
  exportSitrepPdf,
  type BboxQuery,
} from './api';
import { BriefingAdminPanel } from './BriefingAdminPanel';
import { OfficialBriefingBox } from './OfficialBriefingBox';
import { getSessionId } from './lib/operatorActions';

/**
 * B.1 — Briefing-Up render profile (operator-cop-doctrine §2.6 elected-official
 * persona, §3B.7 spec). A second render profile off the same COP data, GENERATED
 * (not authored) from live Response Levels + the latest SITREP.
 *
 * Density inversion vs. the operator COP: large type, generous whitespace, ONE
 * headline metric per hazard, narrative-first (the latest SITREP's executive
 * summary + Recommended Actions are the primary content; the situational map is a
 * supporting visual). The Response Level is the organizing spine (L4 first).
 *
 * Two data sources:
 *   • bbox  (operator mode, via the top-bar mode switch) — full management
 *     surface (mint tokens, manage subscribers, export PDF).
 *   • token (read-only, the Minister's-office deep-link /briefing/:token) — pure
 *     consumption: no actions, no toggles, no operator audit entries.
 */

/** A wide national bbox — the operator-mode fallback before the map reports one. */
const NATIONAL_BBOX: BboxQuery = { west: -141, south: 41, east: -52, north: 84 };

const LEVEL_COLOR: Record<number, string> = {
  0: '#475569',
  1: '#2563eb',
  2: '#f59e0b',
  3: '#ea580c',
  4: '#dc2626',
};

export interface BriefingViewProps {
  /** Read-only token (Minister's-office deep link). Mutually exclusive with bbox. */
  token?: string;
  /** Current-view bbox (operator mode). Ignored when a token is present. */
  bbox?: BboxQuery | null;
  /** True for the token path — hides all actions + the admin sidebar. */
  readOnly: boolean;
  /** Operator-mode only: switch back to the Operator COP. */
  onExitBriefing?: () => void;
}

export function BriefingView({
  token,
  bbox,
  readOnly,
  onExitBriefing,
}: BriefingViewProps): JSX.Element {
  const [snapshot, setSnapshot] = useState<BriefingSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const snap = token
          ? await fetchBriefingTokenPayload(token, signal)
          : await fetchBriefingSnapshot(bbox ?? NATIONAL_BBOX, signal);
        setSnapshot(snap);
      } catch (err) {
        // Ignore the abort from StrictMode's double-mount / a superseded load —
        // a later request will resolve and render.
        if (signal?.aborted || (err as Error)?.name === 'AbortError') return;
        const status = (err as { status?: number }).status;
        setError(
          status === 410
            ? 'This briefing link has expired.'
            : status === 403
              ? 'This briefing link has been revoked.'
              : status === 404
                ? 'Briefing not found.'
                : 'Could not load the briefing.',
        );
      } finally {
        setLoading(false);
      }
    },
    [token, bbox],
  );

  useEffect(() => {
    const ctl = new AbortController();
    void load(ctl.signal);
    return () => ctl.abort();
  }, [load]);

  if (loading && !snapshot) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-950 text-slate-300">
        <p className="text-xl">Loading briefing…</p>
      </div>
    );
  }
  if (error || !snapshot) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-950 text-slate-200">
        <p className="text-2xl" data-testid="briefing-error">
          {error ?? 'Briefing unavailable.'}
        </p>
      </div>
    );
  }

  const lvl = snapshot.response_level_max;
  const bandColor = lvl !== null ? (LEVEL_COLOR[lvl] ?? LEVEL_COLOR[0]) : LEVEL_COLOR[0];
  const sitrep = snapshot.latest_sitrep;
  const incidentId = snapshot.incident_id ?? sitrep?.incident_id ?? null;

  async function onExportPdf(): Promise<void> {
    if (!sitrep) return;
    try {
      const blob = await exportSitrepPdf(sitrep.id, { session_id: getSessionId() });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      /* surfaced via the button's own affordance; non-fatal */
    }
  }

  return (
    <div
      className="flex h-full w-full flex-col overflow-y-auto bg-slate-950 text-slate-100"
      data-testid="briefing-view"
    >
      {/* ── Header band (one-glance severity) ─────────────────────────── */}
      <header style={{ backgroundColor: bandColor }} className="px-10 py-7 text-white">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-widest opacity-90">
              {readOnly ? 'Executive Briefing — Read Only' : 'Executive Briefing'}
            </p>
            <h1 className="mt-1 text-3xl font-bold leading-tight" data-testid="briefing-scope">
              {snapshot.scope_label}
            </h1>
            <p className="mt-2 text-lg">
              {lvl !== null ? `Threat Level ${lvl}` : 'No fused Threat Level in scope'}
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={onExitBriefing}
              className="shrink-0 rounded-md bg-black/25 px-4 py-2 text-sm font-medium hover:bg-black/40"
            >
              ← Operator view
            </button>
          )}
        </div>
      </header>

      {/* ── Headline metrics (one per hazard) ─────────────────────────── */}
      <section className="grid gap-4 px-10 py-7 sm:grid-cols-2 lg:grid-cols-3">
        {snapshot.headline_metrics.map((m, i) => (
          <div
            key={i}
            className="rounded-xl border border-slate-700 bg-slate-900 px-6 py-5"
            data-testid="headline-metric"
          >
            <p className="text-xl font-semibold leading-snug text-slate-50">{m}</p>
          </div>
        ))}
      </section>

      {/* ── Body: narrative-first (primary) + spine & map (supporting) ── */}
      <div className="grid flex-1 gap-8 px-10 pb-10 lg:grid-cols-[3fr_2fr]">
        {/* Primary column — the narrative. */}
        <main className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-50">Situation &amp; Recommended Actions</h2>
          {sitrep ? (
            <>
              <p className="text-base text-slate-400">
                {sitrep.title} · {sitrepReportVersionLabel(sitrep.report_version)} ·{' '}
                {new Date(sitrep.created_at).toLocaleString('en-CA')}
              </p>
              <div>
                <h3 className="mb-3 text-xl font-semibold text-slate-100">Recommended Actions</h3>
                {sitrep.recommended_actions.length > 0 ? (
                  <ul className="space-y-3">
                    {sitrep.recommended_actions.map((a, i) => (
                      <li
                        key={i}
                        className="rounded-lg border-l-4 border-amber-400 bg-slate-900 px-5 py-4 text-lg leading-relaxed"
                        data-testid="recommended-action"
                      >
                        {a}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-lg text-slate-400">Routine monitoring.</p>
                )}
              </div>
              <details className="rounded-lg border border-slate-800 bg-slate-900/60">
                <summary className="cursor-pointer px-5 py-3 text-base font-medium text-slate-300">
                  Full Situation Report
                </summary>
                <pre className="whitespace-pre-wrap px-5 py-4 text-sm leading-relaxed text-slate-300">
                  {sitrep.summary_markdown}
                </pre>
              </details>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => void onExportPdf()}
                  className="rounded-md bg-slate-700 px-5 py-2.5 text-base font-medium text-white hover:bg-slate-600"
                  data-testid="briefing-export-pdf"
                >
                  Export SITREP as PDF
                </button>
              )}
            </>
          ) : (
            <p className="text-lg text-slate-400" data-testid="briefing-no-sitrep">
              No Situation Report has been generated for this scope yet. The briefing reflects the
              live operating picture below.
            </p>
          )}
        </main>

        {/* Supporting column — Response-Level spine + simplified situational map. */}
        <aside className="space-y-6">
          <section>
            <h2 className="mb-3 text-2xl font-bold text-slate-50">By Threat Level</h2>
            {snapshot.sections.length === 0 ? (
              <p className="text-lg text-slate-400">No L3+ hazards in scope.</p>
            ) : (
              <div className="space-y-4">
                {snapshot.sections.map((s) => (
                  <div
                    key={`${s.response_level}-${s.level_name}`}
                    className="rounded-xl border border-slate-700 bg-slate-900"
                    data-testid="briefing-section"
                  >
                    <div
                      style={{ backgroundColor: LEVEL_COLOR[s.response_level] ?? LEVEL_COLOR[0] }}
                      className="rounded-t-xl px-5 py-3 text-white"
                    >
                      <p className="text-lg font-semibold">{s.headline}</p>
                    </div>
                    <ul className="divide-y divide-slate-800">
                      {s.items.map((it, i) => (
                        <li key={i} className="px-5 py-3">
                          <p className="text-base font-medium text-slate-100">{it.label}</p>
                          <p className="text-sm text-slate-400">{it.detail}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Simplified situational map — L3+ items + OC fires only, large
              labelled, with affected-population context. A density-inverted,
              decluttered list view of the map (the map is a supporting visual in
              the briefing profile). */}
          <section>
            <h2 className="mb-3 text-2xl font-bold text-slate-50">Situational Map</h2>
            {snapshot.map_items.length === 0 ? (
              <p className="text-lg text-slate-400">No mapped hazards in scope.</p>
            ) : (
              <ul className="space-y-2" data-testid="briefing-map">
                {snapshot.map_items.slice(0, 40).map((m) => (
                  <li
                    key={`${m.kind}-${m.id}`}
                    className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900 px-4 py-3"
                  >
                    <span
                      style={{
                        backgroundColor:
                          m.response_level !== null
                            ? (LEVEL_COLOR[m.response_level] ?? LEVEL_COLOR[0])
                            : LEVEL_COLOR[0],
                      }}
                      className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
                    />
                    <div>
                      <p className="text-base font-medium text-slate-100">{m.label}</p>
                      {m.affected_population_context && (
                        <p className="text-sm text-slate-400">{m.affected_population_context}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>

      {/* ── Briefing-mode management sidebar (operator mode only, piece F) ── */}
      {!readOnly && (
        <BriefingAdminPanel incidentId={incidentId} currentBbox={bbox ?? NATIONAL_BBOX} />
      )}

      {/* ── #328 Phase 1 — out-of-app official briefing (email the SITREP / record a
          phone contact to a decision-maker outside the ops centre). Operator mode +
          a persisted SITREP only. ── */}
      {!readOnly && sitrep && (
        <OfficialBriefingBox sitrepId={sitrep.id} incidentId={incidentId} />
      )}

      <footer className="px-10 py-4 text-sm text-slate-500">
        Generated {new Date(snapshot.generated_at).toLocaleString('en-CA')} · TSR Platform Briefing-Up
        view · composed from live Threat Levels + the latest SITREP.
      </footer>
    </div>
  );
}
